/**
 * Canonical enums for the Drameeka platform.
 *
 * These are the ONLY allowed values for the matching Postgres enum types —
 * the Drizzle schema in backend/src/db/schema imports from here so the DB and
 * the wire contract can never drift apart.
 */

/** Lifecycle of a title through content moderation. */
export const TITLE_STATUS = [
  'draft',
  'uploaded',
  'transcoding',
  'pending_review',
  'approved',
  'rejected',
  'published',
  'archived',
] as const;
export type TitleStatus = (typeof TITLE_STATUS)[number];

/** Statuses that are visible to end users of a tenant. */
export const PUBLIC_TITLE_STATUSES: readonly TitleStatus[] = ['published'] as const;

/**
 * Who may serve a title. This is the "hybrid catalog" switch:
 * - `platform`  shared catalog, licensable to any tenant via `title_licenses`
 * - `tenant`    a tenant's own original, never visible to anyone else
 * - `both`      tenant original that the platform may also license out
 */
export const TITLE_VISIBILITY = ['platform', 'tenant', 'both'] as const;
export type TitleVisibility = (typeof TITLE_VISIBILITY)[number];

/** How a user came to be allowed to watch an episode. Drives revenue attribution. */
export const ENTITLEMENT_SOURCE = [
  'free',
  'coin_unlock',
  'subscription',
  'promo',
  'grant',
] as const;
export type EntitlementSource = (typeof ENTITLEMENT_SOURCE)[number];

/** Append-only engagement events — the fuel for recommendations. */
export const WATCH_EVENT_TYPE = ['play', 'progress', 'complete', 'skip', 'rewatch'] as const;
export type WatchEventType = (typeof WATCH_EVENT_TYPE)[number];

/** Toggleable user actions surfaced in the UI (like/bookmark/share/save). */
export const USER_ACTION = ['like', 'bookmark', 'share', 'save'] as const;
export type UserAction = (typeof USER_ACTION)[number];

export const MODERATION_DECISION = ['approved', 'rejected', 'needs_changes'] as const;
export type ModerationDecision = (typeof MODERATION_DECISION)[number];

/** Granular permissions on a tenant API key. */
export const API_KEY_SCOPE = [
  'catalog:read',
  'playback:mint',
  'wallet:read',
  'wallet:write',
  'events:write',
  'social:write',
] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPE)[number];

export const TENANT_STATUS = ['pending', 'active', 'suspended', 'cancelled'] as const;
export type TenantStatus = (typeof TENANT_STATUS)[number];

/**
 * Roles on the control plane. `creator` sees only their own titles;
 * `developer` is a tenant's engineer and sees only that tenant's API keys and usage.
 */
export const PLATFORM_ROLE = [
  'owner',
  'admin',
  'moderator',
  'creator',
  'support',
  'developer',
] as const;
export type PlatformRole = (typeof PLATFORM_ROLE)[number];

export const PURCHASE_STATUS = ['pending', 'completed', 'failed', 'refunded'] as const;
export type PurchaseStatus = (typeof PURCHASE_STATUS)[number];

/** Ledger reasons — every coin/gem movement must name one, no exceptions. */
export const WALLET_REASON = [
  'purchase',
  'episode_unlock',
  'daily_reward',
  'ad_reward',
  'promo_grant',
  'refund',
  'admin_adjustment',
] as const;
export type WalletReason = (typeof WALLET_REASON)[number];

/**
 * Drameeka's navigation categories. Kept as a constant so the app's category
 * bar has a stable fallback, but the authoritative list is served per-tenant by
 * GET /v1/categories so tenants can rename or reorder without an app release.
 */
export const DEFAULT_CATEGORIES = [
  'For You',
  'Popular',
  'Werewolf',
  'Billionaire',
  'Romance',
  'Revenge',
  'Urban CEO',
  'Fantasy',
  'Mystery',
] as const;

/** Pseudo-categories that are ranking strategies rather than genre filters. */
export const NON_GENRE_CATEGORIES = ['For You', 'Popular'] as const;
